const EXPORT_JOB_DOWNLOAD_PREFIX = 'export-job-downloaded-';
const EXPORT_JOB_DOWNLOAD_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const EXPORT_JOB_CLEANUP_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

let lastCleanupAt = 0;

const normalizeJobId = (jobId: string): string => String(jobId || '').trim().toLowerCase();

const storageKeyForJob = (jobId: string) => `${EXPORT_JOB_DOWNLOAD_PREFIX}${jobId}`;

const getJobDownloadTimestamp = (jobId: string): number | null => {
  const raw = globalThis.localStorage.getItem(storageKeyForJob(jobId));
  if (!raw) {
    return null;
  }

  const timestamp = Number(raw);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const isExpiredDownloadEntry = (timestamp: number): boolean =>
  Date.now() - timestamp > EXPORT_JOB_DOWNLOAD_TTL_MS;

const cleanupExpiredExportJobEntries = (): void => {
  const now = Date.now();
  const keysToRemove: string[] = [];

  for (let index = 0; index < globalThis.localStorage.length; index += 1) {
    const key = globalThis.localStorage.key(index);
    if (!key?.startsWith(EXPORT_JOB_DOWNLOAD_PREFIX)) {
      continue;
    }

    const timestamp = Number(globalThis.localStorage.getItem(key));
    if (!Number.isFinite(timestamp) || now - timestamp > EXPORT_JOB_DOWNLOAD_TTL_MS) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => globalThis.localStorage.removeItem(key));
};

const maybeCleanupExpiredExportJobEntries = (): void => {
  const now = Date.now();
  if (now - lastCleanupAt < EXPORT_JOB_CLEANUP_INTERVAL_MS) {
    return;
  }

  lastCleanupAt = now;
  cleanupExpiredExportJobEntries();
};

const isJobAlreadyDownloaded = (jobId: string): boolean => {
  const timestamp = getJobDownloadTimestamp(jobId);
  if (timestamp === null) {
    return false;
  }

  if (isExpiredDownloadEntry(timestamp)) {
    globalThis.localStorage.removeItem(storageKeyForJob(jobId));
    return false;
  }

  return true;
};

const markJobAsDownloaded = (jobId: string): void => {
  globalThis.localStorage.setItem(storageKeyForJob(jobId), String(Date.now()));
  cleanupExpiredExportJobEntries();
};

const tryClaimWithStorageRace = async (jobId: string): Promise<boolean> => {
  const key = storageKeyForJob(jobId);
  if (globalThis.localStorage.getItem(key)) {
    return false;
  }

  globalThis.localStorage.setItem(key, TAB_ID);
  await new Promise((resolve) => setTimeout(resolve, 50));

  return globalThis.localStorage.getItem(key) === TAB_ID;
};

/**
 * Runs a download callback for a completed export job in at most one browser tab.
 * SignalR broadcasts to every open tab, so without cross-tab coordination
 * each tab would trigger its own download for the same job.
 */
export const runExclusiveExportJobDownload = async (
  jobId: string,
  downloadFn: () => Promise<void>
): Promise<boolean> => {
  const normalizedJobId = normalizeJobId(jobId);
  if (!normalizedJobId) {
    return false;
  }

  maybeCleanupExpiredExportJobEntries();

  if (isJobAlreadyDownloaded(normalizedJobId)) {
    return false;
  }

  if (navigator.locks?.request) {
    let executed = false;

    await navigator.locks.request(`export-job-download-${normalizedJobId}`, async () => {
      if (isJobAlreadyDownloaded(normalizedJobId)) {
        return;
      }

      await downloadFn();
      markJobAsDownloaded(normalizedJobId);
      executed = true;
    });

    return executed;
  }

  if (isJobAlreadyDownloaded(normalizedJobId)) {
    return false;
  }

  const claimed = await tryClaimWithStorageRace(normalizedJobId);
  if (!claimed) {
    return false;
  }

  try {
    await downloadFn();
    markJobAsDownloaded(normalizedJobId);
    return true;
  } catch {
    globalThis.localStorage.removeItem(storageKeyForJob(normalizedJobId));
    return false;
  }
};
